/** Organization time-zone selector.
 *
 * The platform stores all timestamps in UTC; this sets the tz that
 * beneficiary messages (invite/reminder/confirmation/booking, email + SMS)
 * render times in. A campaign can override it. Set via PATCH
 * /v1/orgs/me/timezone. Gated on org.update (the whole settings page is). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { useTimezones } from "@/lib/useTimezones";
import type { Organization } from "@/lib/types";

export function TimezoneCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const org = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get<Organization>(ApiPaths.orgMe)).data,
  });
  const tzs = useTimezones();

  const save = useMutation({
    mutationFn: async (timezone: string) => {
      const res = await api.patch<Organization>(ApiPaths.orgTimezone, { timezone });
      return res.data;
    },
    onSuccess: (next) => qc.setQueryData(["org-me"], next),
  });

  if (org.isLoading || !org.data) return null;
  const current = org.data.timezone || "UTC";
  const zones = [...(tzs.data ?? ["UTC"])];
  if (!zones.includes(current)) zones.unshift(current);

  return (
    <section className="card space-y-2">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.timezone.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.timezone.cardHint")}</p>
      </header>
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="input max-w-xs text-sm"
          value={current}
          disabled={save.isPending}
          onChange={(e) => save.mutate(e.target.value)}
          aria-label={t("admin.timezone.cardTitle")}
        >
          {zones.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
        {save.isPending && (
          <span className="text-xs text-nb-text-2">{t("admin.timezone.saving")}</span>
        )}
      </div>
      {save.isError && (
        <p className="text-xs text-cvp-danger">
          {(save.error as Error).message ?? t("admin.timezone.saveError")}
        </p>
      )}
    </section>
  );
}
